import { Slot } from 'radix-ui'
import { useId, type ReactNode } from 'react'
import { tAny } from '@/i18n'
import { cn } from '@/utils/cn'

interface FormFieldProps {
  label: ReactNode
  /** The control; receives id / aria-invalid / aria-describedby automatically. */
  children: ReactNode
  error?: string
  hint?: ReactNode
  required?: boolean
  className?: string
  /** Visually hide the label (it stays available to assistive tech). */
  hideLabel?: boolean
  /** Explicit id when the child is a composite control (e.g. Radix select). */
  htmlFor?: string
}

/**
 * Label + control + help text + validation message, wired together for assistive technology.
 */
export function FormField({
  label,
  children,
  error,
  hint,
  required,
  className,
  hideLabel,
  htmlFor,
}: FormFieldProps) {
  const autoId = useId()
  const id = htmlFor ?? `f${autoId}`
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <label
        htmlFor={id}
        className={cn('text-[0.8125rem] font-medium text-ink-soft', hideLabel && 'sr-only')}
      >
        {label}
        {required ? (
          <span className="ml-0.5 text-danger-600" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <Slot.Root
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        aria-required={required || undefined}
      >
        {children}
      </Slot.Root>
      {hint && !error ? (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-xs font-medium text-danger-600" role="alert">
          {/* Client (zod) messages are English keys; server messages arrive already localised. */}
          {tAny(error)}
        </p>
      ) : null}
    </div>
  )
}
